import React, { useState, useRef, useEffect, useCallback } from 'react';
import { mockSaveAnnotation } from '../services/staticData';
import './AnnotationTool.css';

const AnnotationTool = ({ image, onClose, onSave }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [pendingAnnotation, setPendingAnnotation] = useState(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const wasteClasses = [
    { id: 'wet_waste', label: 'Wet Waste', color: '#ff6b6b' },
    { id: 'dry_waste', label: 'Dry Waste', color: '#4ecdc4' },
    { id: 'sanitary_waste', label: 'Sanitary Waste', color: '#45b7d1' },
    { id: 'domestic_hazardous', label: 'Domestic Hazardous Waste', color: '#96ceb4' },
    { id: 'non_recyclable', label: 'Non-recyclable Non-biodegradable Waste', color: '#feca57' },
    { id: 'construction_demolition', label: 'Construction and Demolition Waste', color: '#a55eea' },
    { id: 'slaughterhouse', label: 'Slaughterhouse Waste', color: '#ff9ff3' },
    { id: 'e_waste', label: 'E-Waste', color: '#54a0ff' }
  ];

  const loadImage = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      setImageLoaded(true);
      console.log('AnnotationTool: Successfully loaded image:', image.signed_url || image.image_url || image.url);
    };
    
    img.onerror = (error) => {
      console.error('AnnotationTool: Failed to load image:', image.signed_url || image.image_url || image.url);
      console.error('Error details:', error);
      console.error('This might be due to HTTPS/HTTP mixed content, CORS issues, or invalid URL');
      setImageLoaded(false);
    };
    
    const imageSrc = image.signed_url || image.image_url || image.url || '';
    if (imageSrc) {
      img.src = imageSrc;
    } else {
      console.warn('AnnotationTool: No image URL provided');
      setImageLoaded(false);
    }
  }, [image]);

  useEffect(() => {
    if (image && canvasRef.current) {
      loadImage();
    }
  }, [image, loadImage]);

  const getMousePos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }, []);

  const startDrawing = (e) => {
    if (!imageLoaded) return;
    
    const pos = getMousePos(e);
    const newAnnotation = {
      id: Date.now(),
      startPoint: pos,
      endPoint: pos,
      class: null, // Will be set after drawing
      color: '#cccccc' // Default gray color until category is selected
    };
    
    setCurrentAnnotation(newAnnotation);
    setIsDrawing(true);
    setShowCategoryDropdown(false);
  };

  const draw = (e) => {
    if (!isDrawing || !currentAnnotation) return;
    
    const pos = getMousePos(e);
    const updatedAnnotation = {
      ...currentAnnotation,
      endPoint: pos
    };
    
    setCurrentAnnotation(updatedAnnotation);
    drawAnnotation(updatedAnnotation);
  };

  const stopDrawing = (e) => {
    if (!isDrawing || !currentAnnotation) return;
    
    setIsDrawing(false);
    
    // Check if the box has minimum size (at least 10x10 pixels)
    const width = Math.abs(currentAnnotation.endPoint.x - currentAnnotation.startPoint.x);
    const height = Math.abs(currentAnnotation.endPoint.y - currentAnnotation.startPoint.y);
    
    if (width > 10 && height > 10) {
      // Set as pending annotation and show category dropdown
      setPendingAnnotation(currentAnnotation);
      setShowCategoryDropdown(true);
      setCurrentAnnotation(null);
    } else {
      setCurrentAnnotation(null);
      redrawCanvas();
    }
  };

  const drawAnnotation = (annotation) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Redraw the original image
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      // Draw existing annotations
      annotations.forEach(ann => {
        drawSingleAnnotation(ann);
      });
      
      // Draw pending annotation if exists
      if (pendingAnnotation) {
        drawSingleAnnotation(pendingAnnotation);
      }
      
      // Draw current annotation
      if (annotation) {
        drawSingleAnnotation(annotation);
      }
    };
    img.src = image.signed_url || image.image_url || image.url || '';
  };

  const drawSingleAnnotation = (annotation) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Calculate rectangle coordinates
    const x = Math.min(annotation.startPoint.x, annotation.endPoint.x);
    const y = Math.min(annotation.startPoint.y, annotation.endPoint.y);
    const width = Math.abs(annotation.endPoint.x - annotation.startPoint.x);
    const height = Math.abs(annotation.endPoint.y - annotation.startPoint.y);
    
    // Draw rectangle outline
    ctx.strokeStyle = annotation.color;
    ctx.lineWidth = 3;
    ctx.strokeRect(x, y, width, height);
    
    // Add class label (only if class is selected)
    if (annotation.class) {
      ctx.fillStyle = annotation.color;
      ctx.font = '14px Arial';
      ctx.fillText(annotation.class, x + 5, y - 5);
    } else {
      // Show "Select Category" for pending annotations
      ctx.fillStyle = '#666666';
      ctx.font = '12px Arial';
      ctx.fillText('Select Category', x + 5, y - 5);
    }
  };

  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Redraw the original image
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      // Draw all annotations
      annotations.forEach(annotation => {
        drawSingleAnnotation(annotation);
      });
      
      // Draw pending annotation if exists
      if (pendingAnnotation) {
        drawSingleAnnotation(pendingAnnotation);
      }
    };
    img.src = image.signed_url || image.image_url || image.url || '';
  };

  const handleCategorySelect = (classId) => {
    if (!pendingAnnotation) return;
    
    const selectedWasteClass = wasteClasses.find(c => c.id === classId);
    const completedAnnotation = {
      ...pendingAnnotation,
      class: classId,
      color: selectedWasteClass.color
    };
    
    // Add to annotations and clear pending
    setAnnotations([...annotations, completedAnnotation]);
    setPendingAnnotation(null);
    setShowCategoryDropdown(false);
    
    // Redraw canvas to show the completed annotation
    redrawCanvas();
  };

  const cancelPendingAnnotation = () => {
    setPendingAnnotation(null);
    setShowCategoryDropdown(false);
    redrawCanvas();
  };

  const deleteAnnotation = (annotationId) => {
    const updatedAnnotations = annotations.filter(ann => ann.id !== annotationId);
    setAnnotations(updatedAnnotations);
    
    // Redraw canvas
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      updatedAnnotations.forEach(annotation => {
        drawSingleAnnotation(annotation);
      });
    };
    img.src = image.signed_url || image.image_url || image.url || '';
  };

  const calculateBoundingBox = (annotation) => {
    if (!annotation.startPoint || !annotation.endPoint) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    
    const x = Math.min(annotation.startPoint.x, annotation.endPoint.x);
    const y = Math.min(annotation.startPoint.y, annotation.endPoint.y);
    const width = Math.abs(annotation.endPoint.x - annotation.startPoint.x);
    const height = Math.abs(annotation.endPoint.y - annotation.startPoint.y);
    
    return { x, y, width, height };
  };

  const handleSave = async () => {
    if (annotations.length === 0) {
      alert('No annotations to save');
      return;
    }

    setSaving(true);
    
    // Create category mapping
    const categoryMapping = {
      'wet_waste': { id: 1, name: 'Wet Waste' },
      'dry_waste': { id: 2, name: 'Dry Waste' },
      'sanitary_waste': { id: 3, name: 'Sanitary Waste' },
      'domestic_hazardous': { id: 4, name: 'Domestic Hazardous Waste' },
      'non_recyclable': { id: 5, name: 'Non-recyclable Non-biodegradable Waste' },
      'construction_demolition': { id: 6, name: 'Construction and Demolition Waste' },
      'slaughterhouse': { id: 7, name: 'Slaughterhouse Waste' },
      'e_waste': { id: 8, name: 'E-Waste' }
    };
    
    // Create the new JSON format
    const bboxData = annotations.map((annotation) => {
      const bbox = calculateBoundingBox(annotation);
      const category = categoryMapping[annotation.class];
      
      return {
        category_id: category.id,
        category_name: category.name,
        bbox: [bbox.x, bbox.y, bbox.width, bbox.height]
      };
    });
    
    const annotationData = {
      image_id: image.id,
      annotations: bboxData
    };
    
    // Create the API payload format
    const apiPayload = {
      annotation_image_id: image.id,
      annotation_json: annotationData
    };
    
    try {
      await mockSaveAnnotation(apiPayload);
      onSave && onSave(annotationData);
      alert('Annotations saved successfully (demo mode)!');
    } catch (error) {
      console.error('Error saving annotations:', error);
      alert('Failed to save annotations. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!image) {
    return (
      <div className="annotation-tool-modal">
        <div className="modal-content">
          <p>No image selected</p>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="annotation-tool-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Annotation Tool</h2>
          <div className="header-actions">
            <button onClick={onClose} className="close-btn">✕</button>
          </div>
        </div>

        {showCategoryDropdown && pendingAnnotation && (
          <div className="category-dropdown">
            <h4>Select Category for this annotation:</h4>
            <div className="category-options">
              {wasteClasses.map(wasteClass => (
                <button
                  key={wasteClass.id}
                  className="category-option"
                  onClick={() => handleCategorySelect(wasteClass.id)}
                  style={{ borderColor: wasteClass.color }}
                >
                  <span className="category-color" style={{ backgroundColor: wasteClass.color }}></span>
                  {wasteClass.label}
                </button>
              ))}
            </div>
            <button onClick={cancelPendingAnnotation} className="cancel-btn">
              Cancel
            </button>
          </div>
        )}

        <div className="toolbar">
          <div className="toolbar-section">
            <span>Instructions: Click and drag to draw boxes around waste objects</span>
          </div>
          <div className="toolbar-actions">
            <button onClick={handleSave} disabled={saving || annotations.length === 0} className="save-btn">
              {saving ? 'Saving...' : `Save (${annotations.length})`}
            </button>
          </div>
        </div>

        <div className="canvas-container">
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            className="annotation-canvas"
          />
          {!imageLoaded && (
            <div className="image-loading">
              <div className="loading-spinner"></div>
              <p>Loading image...</p>
            </div>
          )}
        </div>

        {annotations.length > 0 && (
          <div className="annotations-list">
            <h4>Annotations ({annotations.length})</h4>
            <div className="annotations-items">
              {annotations.map((annotation, index) => (
                <div key={annotation.id} className="annotation-item">
                  <span className="annotation-class" style={{ color: annotation.color }}>
                    {annotation.class}
                  </span>
                  <span className="annotation-id">#{index + 1}</span>
                  <button onClick={() => deleteAnnotation(annotation.id)} className="delete-btn">
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnotationTool;
